import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { ArrowUp, FileText } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type { ChatPanelInputProps } from "./types";

export function ChatPanelInput({
  placeholder,
  onSend,
  context,
}: ChatPanelInputProps) {
  const { t } = useTranslation(["chat", "common"]);
  const resolvedPlaceholder = placeholder ?? t("input.placeholder");
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = value.trim();

  function handleSend() {
    if (!trimmed) return;
    onSend(trimmed);
    setValue("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const ContextIcon = context?.icon ?? FileText;
  const hasContent = trimmed.length > 0;

  return (
    <div className="px-4 pb-4 pt-2 relative">
      {context && (
        <div className="flex items-center gap-1.5 rounded-lg bg-accent/8 border border-accent/15 max-w-min px-2.5 py-1 mb-2">
          <ContextIcon size={10} className="text-accent/60 shrink-0" />
          <span className="text-[10.5px] font-medium text-secondary truncate">
            {context.label}
          </span>
        </div>
      )}
      <div className="relative flex items-center gap-2 rounded-2xl border border-border/50 bg-white/80 px-4 py-2.5 shadow-sm focus-within:border-accent/40 focus-within:shadow-[0_0_0_3px_rgba(146,208,206,0.1)] transition-all duration-200">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={resolvedPlaceholder}
          className="flex-1 bg-transparent text-[13px] text-primary placeholder:text-muted/70 outline-none"
        />
        <button
          type="button"
          onClick={handleSend}
          disabled={!hasContent}
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-all duration-200 cursor-pointer ${
            hasContent
              ? "bg-accent text-white shadow-sm hover:bg-accent/90 hover:scale-105"
              : "bg-black/5 text-muted/40"
          }`}
          aria-label={t("common:ui.chatPanel.sendMessage")}
        >
          <ArrowUp size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
